import React, { useEffect, useRef, useState } from 'react';
import './Nickname.css';
import { useSocket } from '../context/SocketContext';
import ProfileImage from '../components/ProfileImage';
import ProfilePicker from '../components/ProfilePicker';
import BrandFooter from '../components/BrandFooter';
import { randomProfileIndex, profileImageUrl, imageUrlToBase64 } from '../lib/profileImage';

const pad2 = (n) => String(n).padStart(2, '0');
const randomNickname = () => `No.${pad2(1 + Math.floor(Math.random() * 99))}`;

function EditIcon() {
  return (
    <svg
      className="nickname-edit-icon"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </svg>
  );
}

/**
 * Nickname / registration screen (status READY).
 * - Not registered -> profile picker + nickname input + 등록 button
 * - Registered     -> the chosen profile/nickname, frozen, + waiting message
 *
 * On register the chosen image is converted to base64 and (with the
 * nickname + index) persisted to localStorage by SocketContext.
 */
const Nickname = () => {
  const { userInfo, registerUser, isConnected } = useSocket();
  const isRegistered = !!userInfo.nickname;

  const [profileIdx, setProfileIdx] = useState(() => randomProfileIndex());
  const [nick, setNick] = useState(() => randomNickname());
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  // iOS Safari/Chrome: when the on-screen keyboard opens, the visualViewport
  // resizes. The CSS already pins the screen to --app-height, but as a
  // safety net we also scroll the focused input into view once the keyboard
  // animation settles. Without this, on very short viewports (e.g. iPhone
  // SE in landscape) the input can still sit a few px below the keyboard.
  const inputRef = useRef(null);
  useEffect(() => {
    if (!window.visualViewport) return;
    const scrollInputIntoView = () => {
      const el = inputRef.current;
      if (!el) return;
      if (document.activeElement !== el) return;
      try {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      } catch {
        try { el.scrollIntoView(false); } catch {}
      }
    };
    const onResize = () => {
      // Defer so layout reflects the new --app-height first.
      window.setTimeout(scrollInputIntoView, 80);
    };
    window.visualViewport.addEventListener('resize', onResize);
    return () => window.visualViewport.removeEventListener('resize', onResize);
  }, []);

  const handleInputFocus = () => {
    // Initial focus scroll (in case the keyboard opens with no visualViewport
    // resize event, e.g. older WebKit).
    window.setTimeout(() => {
      const el = inputRef.current;
      if (!el || document.activeElement !== el) return;
      try { el.scrollIntoView({ block: 'center', behavior: 'smooth' }); } catch {}
    }, 250);
  };

  const handleRegister = async () => {
    if (busy) return;
    if (!isConnected) {
      alert('서버에 연결 중입니다. 잠시 후 다시 시도해 주세요.');
      return;
    }
    setBusy(true);

    const name = nick.trim() || randomNickname();
    let base64 = null;
    try {
      base64 = await imageUrlToBase64(profileImageUrl(profileIdx));
    } catch (e) {
      // Asset missing — register with the index only; image still renders
      // from /assets/profiles/{n}.png on the game screens.
      console.warn('[Nickname] profile base64 conversion failed:', e?.message || e);
    }
    registerUser(name, profileIdx, base64);

    // Safety: if the server never acks, let the user retry.
    setTimeout(() => setBusy(false), 3000);
  };

  // ---- Registered: waiting for the game to start ----
  if (isRegistered) {
    return (
      <div className="nickname-screen">
        <div className="nickname-inner">
          <h1 className="nickname-title">등록이 완료되었어요</h1>

          <div className="nickname-profile-wrap">
            <ProfileImage
              index={userInfo.profileIndex}
              src={userInfo.profileImage}
              className="nickname-profile-img"
              alt={userInfo.nickname}
            />
          </div>

          <div className="nickname-field-label">닉네임</div>
          <div className="nickname-box static">{userInfo.nickname}</div>

          <div className="nickname-waiting-msg">
            등록 완료!<br />
            게임 시작을 기다려주세요.
          </div>
          <div className="nickname-waiting-dots" aria-hidden="true">
            <span /><span /><span />
          </div>
        </div>
        <BrandFooter />
      </div>
    );
  }

  // ---- Not registered: pick profile + nickname ----
  return (
    <div className="nickname-screen">
      <div className="nickname-inner">
        <h1 className="nickname-title">닉네임을 설정해주세요</h1>

        <div className="nickname-profile-wrap">
          <button
            type="button"
            className="nickname-profile-tap"
            onClick={() => setPickerOpen(true)}
            aria-label="프로필 이미지 선택"
          >
            <ProfileImage
              index={profileIdx}
              className="nickname-profile-img"
              alt="프로필 이미지"
            />
          </button>
          <button
            type="button"
            className="nickname-edit-btn"
            onClick={() => setPickerOpen(true)}
            aria-label="프로필 이미지 변경"
          >
            <EditIcon />
          </button>
        </div>

        <div className="nickname-field-label">닉네임</div>
        <div className="nickname-box">
          <input
            ref={inputRef}
            className="nickname-input"
            type="text"
            value={nick}
            onChange={(e) => setNick(e.target.value)}
            onFocus={handleInputFocus}
            maxLength={10}
            inputMode="text"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
            enterKeyHint="done"
            aria-label="닉네임"
          />
        </div>

        <button
          type="button"
          className={`register-btn ${isConnected && !busy ? '' : 'disabled'}`}
          onClick={handleRegister}
          aria-disabled={!isConnected || busy}
        >
          <span className="register-btn-text">{busy ? '등록 중…' : '등록'}</span>
        </button>
      </div>

      {pickerOpen && (
        <ProfilePicker
          current={profileIdx}
          onSelect={setProfileIdx}
          onClose={() => setPickerOpen(false)}
        />
      )}

      <BrandFooter />
    </div>
  );
};

export default Nickname;
